import React from "react";

import TableSortLabel from "@mui/material/TableSortLabel";

import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import Text from "@src/sharedComponents/Typography/Text";

import { useTableColumnWidths } from "./Table.context";
import { flipTableSortOrder } from "./TableHeader.utils";

import styles from "./TableHeader.module.css";

const MIN_COLUMN_WIDTH = 120;

export type TableSortOrder = "asc" | "desc";

export interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
  columnIndex: number;
  sortable?: boolean;
  active?: boolean;
  sortOrder?: TableSortOrder;
  onClick?: (sortOrder: TableSortOrder) => void;
}

export const TableHeader: React.FC<TableHeaderProps> = ({
  children,
  className,
  columnIndex,
  sortable,
  active,
  sortOrder,
  onClick,
}) => {
  const { columnWidths, setColumnWidths, resizable } = useTableColumnWidths();

  const handleMouseDown = (event: React.MouseEvent<HTMLSpanElement>): void => {
    event.preventDefault();
    // The starting X position of the mouse
    const startX = event.clientX;
    // The starting width of this column (parse out number if it's in px)
    const currentWidthPx =
      parseInt(columnWidths[columnIndex], 10) || MIN_COLUMN_WIDTH;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = currentWidthPx + delta;

      if (newWidth > MIN_COLUMN_WIDTH) {
        setColumnWidths((columnWidths) =>
          Object.assign([], columnWidths, { [columnIndex]: newWidth + "px" })
        );
      }
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    // Attach events to the document so we can track mouse outside the header
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleDoubleClick = (): void => {
    setColumnWidths((columnWidths) =>
      Object.assign([], columnWidths, {
        [columnIndex]: MIN_COLUMN_WIDTH + "px",
      })
    );
  };

  return (
    <div className={toClassName(styles.header, className)}>
      {sortable ? (
        <TableSortLabel
          classes={{
            root: styles.root,
            icon: styles.icon,
          }}
          active={active}
          direction={sortOrder}
          onClick={() => onClick?.(flipTableSortOrder(sortOrder!))}
        >
          <Text variant="title3" color={active ? "text" : "textSubtle"}>
            {children}
          </Text>
        </TableSortLabel>
      ) : (
        <Text variant="title3" color="textSubtle">
          {children}
        </Text>
      )}
      {resizable && (
        <div
          className={styles.resizeContainer}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
        >
          <span className={styles.resizeHandle} />
        </div>
      )}
    </div>
  );
};
